import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import FormLabel from '@material-ui/core/FormLabel';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import {usePreferencesState, useUpdatePreferences} from '@tracker/shared/src/Preferences/PreferencesProvider';
import {favoritesTeamId, unscheduledTeamId} from '@tracker/shared/src/Scheduling/Teams';
import {ClientServiceTeam} from "@tracker/shared/src/Scheduling/Utils/EmployeeSchedulesCompiler";
import React from 'react';

export const drawerWidth = 300;

const useStyles = makeStyles(theme => ({
    root: {
        width: drawerWidth,
        flexShrink: 0,
    },
    paper: {
        width: drawerWidth,
    },
    spacer: {
        flexGrow: 1,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(0, 1),
        ...theme.mixins.toolbar,
    },
    formControl: {
        padding: theme.spacing(2),
    },
}));

type Props = {
    open : boolean;
    onClose : () => void;
    filtersActive : boolean;
    onFiltersActiveChange : (active : boolean) => void;
    csTeams : ClientServiceTeam[];
};

export const practiceAreaOptions = [
    'FileMaker',
    'ActualizeIt',
    'Cloud Native Applications',
    'Project Management',
    'Salesforce',
    'AWS',
];

const FilterDrawer : React.FC<Props> = ({open, onClose, filtersActive, onFiltersActiveChange, csTeams} : Props) => {
    const classes = useStyles();
    const {preferences} = usePreferencesState();
    const updatePreferences = useUpdatePreferences();

    const handleTeamChange = (teamId : number, checked : boolean) => {
        if (!checked) {
            updatePreferences({scheduler: {selectedTeams: preferences.scheduler.selectedTeams.filter(
                selectedTeamId => selectedTeamId !== teamId
            )}});
        } else {
            updatePreferences({scheduler: {selectedTeams: [...preferences.scheduler.selectedTeams, teamId]}});
        }
    };

    const handlePracticeAreaChange = (practiceArea : string, checked : boolean) => {
        if (!checked) {
            updatePreferences({scheduler: {selectedPracticeAreas: preferences.scheduler.selectedPracticeAreas.filter(
                selectedPracticeArea => selectedPracticeArea !== practiceArea
            )}});
        } else {
            updatePreferences({scheduler: {selectedPracticeAreas: [
                ...preferences.scheduler.selectedPracticeAreas,
                practiceArea,
            ]}});
        }
    };

    const handelOnlyCstMembersChange = (status : boolean) => {
        updatePreferences({scheduler: {onlyCstMembers : status}});
    }

    const handelMonthlyCapacityTotals = (show : boolean) => {
        updatePreferences({scheduler: {showMonthlyCapacityTotals : show}});
    }

    const {selectedTeams, showMonthlyCapacityTotals} = preferences.scheduler;
    const filterFavorites = selectedTeams.includes(favoritesTeamId);
    const filterUnscheduled = selectedTeams.includes(unscheduledTeamId);

    const drawer = (
        <>
            <div className={classes.header}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={filtersActive}
                            onChange={event => onFiltersActiveChange(event.target.checked)}
                        />
                    }
                    label="Filters"
                />
                <div className={classes.spacer}/>
                <IconButton onClick={onClose}>
                    <ChevronLeftIcon/>
                </IconButton>
            </div>
            <Divider/>
            <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel component="legend">CS Teams</FormLabel>
                <FormGroup>
                    {csTeams.map(team => {
                        const checked = selectedTeams.includes(team.id);
                        return (
                            <Tooltip title={team.abbreviation} key={team.id}>
                                <FormControlLabel
                                    disabled={!filtersActive}
                                    control={(
                                        <Checkbox
                                            size="small"
                                            checked={checked}
                                            onChange={() => handleTeamChange(team.id, !checked)}
                                        />
                                    )}
                                    label={team.name}
                                />
                            </Tooltip>
                        );
                    })}
                </FormGroup>
            </FormControl>
            <Divider/>
            <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel component="legend">Include</FormLabel>
                <FormGroup>
                    <FormControlLabel
                        disabled={!filtersActive}
                        control={(
                            <Checkbox
                                size="small"
                                checked={preferences.scheduler.onlyCstMembers}
                                onChange={() => handelOnlyCstMembersChange(!preferences.scheduler.onlyCstMembers)}
                            />
                        )}
                        label="Only CST Members"
                    />
                    <FormControlLabel
                        disabled={!filtersActive}
                        control={(
                            <Checkbox
                                size="small"
                                checked={filterFavorites}
                                onChange={() => handleTeamChange(favoritesTeamId, !filterFavorites)}
                            />
                        )}
                        label="Favorites"
                    />
                    <FormControlLabel
                        disabled={!filtersActive}
                        control={(
                            <Checkbox
                                size="small"
                                checked={filterUnscheduled}
                                onChange={() => handleTeamChange(unscheduledTeamId, !filterUnscheduled)}
                            />
                        )}
                        label="Unscheduled Subcontractors"
                    />
                    <FormControlLabel
                        disabled={!filtersActive}
                        control={(
                            <Checkbox
                                size="small"
                                checked={showMonthlyCapacityTotals}
                                onChange={() => handelMonthlyCapacityTotals(!showMonthlyCapacityTotals)}
                            />
                        )}
                        label="Monthly Capacity Totals"
                    />
                </FormGroup>
            </FormControl>
            <Divider/>
            <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel component="legend">Practice Areas</FormLabel>
                <FormGroup>
                    {practiceAreaOptions.map(practiceArea => {
                        const checked = preferences.scheduler.selectedPracticeAreas.includes(practiceArea);

                        return (
                            <FormControlLabel
                                key={practiceArea}
                                disabled={!filtersActive}
                                control={(
                                    <Checkbox
                                        checked={checked}
                                        onChange={() => handlePracticeAreaChange(practiceArea, !checked)}
                                        name={practiceArea}
                                        size="small"
                                    />
                                )}
                                label={practiceArea}
                            />
                        );
                    })}
                </FormGroup>
            </FormControl>
        </>
    );

    return (
        <>
            <Hidden lgUp implementation="js">
                <Drawer
                    className={classes.root}
                    variant="temporary"
                    anchor="left"
                    open={open}
                    onClose={onClose}
                    classes={{
                        paper: classes.paper,
                    }}
                >
                    {drawer}
                </Drawer>
            </Hidden>

            <Hidden mdDown implementation="js">
                <Drawer
                    className={classes.root}
                    variant="persistent"
                    anchor="left"
                    open={open}
                    classes={{
                        paper: classes.paper,
                    }}
                >
                    {drawer}
                </Drawer>
            </Hidden>
        </>
    );
};

export default FilterDrawer;
